/**
 * 🚀 Virtual Membership System - Zero Sync Risk
 * 
 * This system treats sections as fully functional groups without storing duplicate data.
 * - Sections have NO membership records in groupMembers collection
 * - Section membership is calculated dynamically from child groups
 * - All group features (chat, leaderboards, etc.) work by querying child groups
 * - Zero synchronization = Zero sync failure risk
 */

import { db } from '../config';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc 
} from 'firebase/firestore';
import { memberDisplayName, pickMemberNameFields, type MemberNameFields } from '@/lib/groups/memberName';

export interface VirtualMember extends MemberNameFields {
  id: string;
  name: string;
  email: string;
  photoURL?: string;
  role: string;
  joinedAt: Date;
  sourceGroups: string[]; // Which child groups they're a member of
}

/**
 * Get virtual members for a section (calculated from child groups)
 * or actual members for a regular group
 */
export async function getGroupMembersVirtual(groupId: string): Promise<VirtualMember[]> {
  try {
    // Get the group to check if it's a section
    const groupDoc = await getDoc(doc(db, 'supportGroups', groupId));
    if (!groupDoc.exists()) return [];
    
    const groupData = groupDoc.data();
    
    if (groupData.groupType === 'parent') {
      // Section: calculate virtual members from child groups
      return await getVirtualSectionMembers(groupId, groupData.organization);
    } else {
      // Regular group: get actual members
      return await getActualGroupMembers(groupId);
    }
  } catch (error) {
    console.error('Error getting virtual group members:', error);
    return [];
  }
}

/**
 * Get virtual members for a section by aggregating from child groups
 */
async function getVirtualSectionMembers(sectionId: string, organizationId: string): Promise<VirtualMember[]> {
  try {
    // Get all child groups under this section
    const childGroupsQuery = query(
      collection(db, 'supportGroups'),
      where('parentGroupId', '==', sectionId),
      where('organization', '==', organizationId)
    );
    const childGroups = await getDocs(childGroupsQuery);
    
    const memberMap = new Map<string, VirtualMember>();
    
    // Collect members from all child groups
    for (const childDoc of childGroups.docs) {
      const childGroupId = childDoc.id;
      const childGroupName = childDoc.data().name;
      
      // Get members of this child group
      const membersQuery = query(
        collection(db, 'groupMembers'),
        where('groupId', '==', childGroupId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      for (const memberDoc of membersSnapshot.docs) {
        const memberData = memberDoc.data();
        const userId = memberData.userId;
        
        // Get user details
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (!userDoc.exists()) continue;
        
        const userData = userDoc.data();
        
        if (memberMap.has(userId)) {
          // User already in map, add this group to their source groups
          const existingMember = memberMap.get(userId)!;
          existingMember.sourceGroups.push(childGroupName);
        } else {
          // New user, add to map
          memberMap.set(userId, {
            id: userId,
            ...pickMemberNameFields(userData),
            name: memberDisplayName(pickMemberNameFields(userData), 'ar', 'Unknown'),
            email: userData.email || '',
            photoURL: userData.photoURL,
            role: memberData.role || 'member',
            joinedAt: memberData.joinedAt?.toDate() || new Date(),
            sourceGroups: [childGroupName]
          });
        }
      }
    }
    
    // Convert map to array and sort by join date
    return Array.from(memberMap.values()).sort(
      (a, b) => a.joinedAt.getTime() - b.joinedAt.getTime()
    );
    
  } catch (error) {
    console.error('Error getting virtual section members:', error);
    return [];
  }
}

/**
 * Get actual members for a regular group
 */
async function getActualGroupMembers(groupId: string): Promise<VirtualMember[]> {
  try {
    const membersQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', groupId)
    );
    const membersSnapshot = await getDocs(membersQuery);
    
    const members: VirtualMember[] = [];
    
    for (const memberDoc of membersSnapshot.docs) {
      const memberData = memberDoc.data();
      const userId = memberData.userId;
      
      // Get user details
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) continue;
      
      const userData = userDoc.data();
      
      members.push({
        id: userId,
        ...pickMemberNameFields(userData),
        name: memberDisplayName(pickMemberNameFields(userData), 'ar', 'Unknown'),
        email: userData.email || '',
        photoURL: userData.photoURL,
        role: memberData.role || 'member',
        joinedAt: memberData.joinedAt?.toDate() || new Date(),
        sourceGroups: [] // Regular groups don't have source groups
      });
    }
    
    return members.sort((a, b) => a.joinedAt.getTime() - b.joinedAt.getTime());
    
  } catch (error) {
    console.error('Error getting actual group members:', error);
    return [];
  }
}

/**
 * Get virtual member count for any group (section or regular)
 */
export async function getGroupMemberCountVirtual(groupId: string): Promise<number> {
  try {
    // Get the group to check if it's a section
    const groupDoc = await getDoc(doc(db, 'supportGroups', groupId));
    if (!groupDoc.exists()) return 0;
    
    const groupData = groupDoc.data();
    
    if (groupData.groupType === 'parent') {
      // Section: calculate virtual count from child groups
      return await getVirtualSectionMemberCount(groupId, groupData.organization);
    } else {
      // Regular group: get actual count
      const membersQuery = query(
        collection(db, 'groupMembers'),
        where('groupId', '==', groupId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      return membersSnapshot.docs.length;
    }
  } catch (error) {
    console.error('Error getting virtual group member count:', error);
    return 0;
  }
}

/**
 * Get virtual member count for a section
 */
async function getVirtualSectionMemberCount(sectionId: string, organizationId: string): Promise<number> {
  try {
    // Get all child groups under this section
    const childGroupsQuery = query(
      collection(db, 'supportGroups'),
      where('parentGroupId', '==', sectionId),
      where('organization', '==', organizationId)
    );
    const childGroups = await getDocs(childGroupsQuery);
    
    const uniqueMembers = new Set<string>();
    
    // Collect unique member IDs from all child groups
    for (const childDoc of childGroups.docs) {
      const childGroupId = childDoc.id;
      
      const membersQuery = query(
        collection(db, 'groupMembers'),
        where('groupId', '==', childGroupId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      membersSnapshot.docs.forEach(doc => {
        uniqueMembers.add(doc.data().userId);
      });
    }
    
    return uniqueMembers.size;
    
  } catch (error) {
    console.error('Error getting virtual section member count:', error);
    return 0;
  }
}

/**
 * Check if a user is a virtual member of a section or actual member of a group
 */
export async function isVirtualMember(groupId: string, userId: string): Promise<boolean> {
  try {
    // Get the group to check if it's a section
    const groupDoc = await getDoc(doc(db, 'supportGroups', groupId));
    if (!groupDoc.exists()) return false;
    
    const groupData = groupDoc.data();
    
    if (groupData.groupType === 'parent') {
      // Section: check if user is member of any child group
      const childGroupsQuery = query(
        collection(db, 'supportGroups'),
        where('parentGroupId', '==', groupId),
        where('organization', '==', groupData.organization)
      );
      const childGroups = await getDocs(childGroupsQuery);
      
      for (const childDoc of childGroups.docs) {
        const membersQuery = query(
          collection(db, 'groupMembers'),
          where('groupId', '==', childDoc.id),
          where('userId', '==', userId)
        );
        const membership = await getDocs(membersQuery);
        
        if (!membership.empty) {
          return true; // User is member of at least one child group
        }
      }
      
      return false;
    } else {
      // Regular group: check actual membership
      const membersQuery = query(
        collection(db, 'groupMembers'),
        where('groupId', '==', groupId),
        where('userId', '==', userId)
      );
      const membership = await getDocs(membersQuery);
      return !membership.empty;
    }
  } catch (error) {
    console.error('Error checking virtual membership:', error);
    return false;
  }
}